import React, { useCallback } from 'react'
import { Segment, Header, Container } from 'semantic-ui-react'
import DataTable from '../../components/data_table'
import { dateToString, filterTypes, handleRedirect, isObject, navigate, NotFoundError } from '../../helpers'
import { getTaskExecutions, getTasks } from '../../api/tasks'
import { Await, defer, useNavigate, useRouteLoaderData } from 'react-router-dom'
import { usePreloadedEntityDispatcher } from '../root'

export async function loader({ request: { signal } }) {
	const tasks = getTasks({}, { signal }).catch(e => {
		if (isObject(e) && isObject(e.res) && typeof e.res.redirect === 'string') {
			return null
		} else if (isObject(e) && isObject(e.res) && isObject(e.res.error) && e.res.error.code === 'not_found') {
			return Promise.reject(new NotFoundError('Fant ingen bakgrunnsjobber: ' + e.message))
		} else {
			return Promise.reject(new Error('Kunne ikke laste inn bakgrunnsjobber: ' + e.message))
		}
	})
	return defer({ tasks })
}

function TaskExecutionsDataTable({ loading, metadata: { states, tasks } }) {
	const routerNavigateFn = useNavigate()
	const preloadEntity = usePreloadedEntityDispatcher()
	const apiSearch = useCallback(async (query, options, requestOptions) => {
		const res = await getTaskExecutions(query, options, requestOptions)
		handleRedirect(res, routerNavigateFn)
		return res
	}, [routerNavigateFn])

	const taskOptions = Object.keys(tasks).map(task => ({ value: task, text: tasks[task].name }))
	const stateOptions = Object.keys(states).map(state => ({ value: state, text: states[state].name }))
	const taskStateOptions = Object.keys(tasks).flatMap(task => Object.keys(tasks[task].task_states).filter(state => state !== '0').map(state => ({ value: state, text: tasks[task].name + ': ' + (tasks[task].task_states[state].name ?? ('Ukjent jobb-status (' + state + ')')) })))

	return <DataTable
		columns={[
			{ key: 'id', text: '#', visible: true, sortable: true, filterType: filterTypes.search },
			{ key: 'task', text: 'Jobb', visible: true, sortable: true, filterType: filterTypes.selection, filterOptions: taskOptions },
			{ key: 'object_display_name', text: 'Objekt', visible: true },
			{ key: 'state', text: 'Status', visible: true, sortable: true, filterType: filterTypes.selection, filterOptions: stateOptions },
			{ key: 'task_state', text: 'Jobb-status', visible: true, sortable: true, filterType: filterTypes.selection, filterOptions: taskStateOptions },
			{ key: 'started_at', text: 'Startet', visible: true, sortable: true, filterType: filterTypes.date },
			{ key: 'stopped_at', text: 'Stoppet', visible: true, sortable: true, filterType: filterTypes.date },
			{ key: 'input', text: 'Input', visible: false, filterType: filterTypes.search },
			{ key: 'output', text: 'Output', visible: false, filterType: filterTypes.search },
		]}
		loading={loading}
		defaultOrder={{'id': 'DESC'}}
		apiSearch={apiSearch}
		onClickRow={(data, index, { ctrlKey, shiftKey }) => {
			// preload execution data
			preloadEntity(data)
			// navigate to execution
			const url = '/tasks/executions/' + encodeURIComponent(data.id)
			const target = (ctrlKey || shiftKey) ? '_blank' : '_self'
			navigate(url, target, {}, routerNavigateFn)
		}}
		renderRow={data => [
			data.id,
			data.task_display_name ?? 'n/a',
			data.object_display_name ?? 'n/a',
			data.state_display_name ?? 'n/a',
			data.task_state_display_name ?? 'n/a',
			data.started_at === null ? 'Ikke startet' : dateToString(data.started_at, true, true),
			data.stopped_at === null ? 'Ikke stoppet' : dateToString(data.stopped_at, true, true),
		]}
	/>
}

export default function TaskExecutionsPage() {
	const { tasks } = useRouteLoaderData('tasks')

	return <>
		<Segment vertical padded="very">
			<Container>
				<Header size="huge">
					Bakgrunnsjobber
					<Header.Subheader>Liste over kjøringer av bakgrunnsjobber</Header.Subheader>
				</Header>

				<React.Suspense
					fallback={<TaskExecutionsDataTable metadata={{states: {}, tasks: {}}} loading />}
				>
					<Await resolve={tasks}>{res => isObject(res) && isObject(res.data) ?
						<TaskExecutionsDataTable metadata={res.data} />
					: null}</Await>
				</React.Suspense>
			</Container>
		</Segment>
	</>
}